import React, { useState, useEffect, useCallback } from 'react';
import { Button, Chip, Container, Dialog, Grid, Paper, Stack, Typography, Checkbox, Box } from '@mui/material';
import { FormProvider, SelectTag, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, request, BASE_URL } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { useDoctorScope } from '../../../../../customHooks/useDoctorScope';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'react-toastify';

export const DoctorLedgerReport = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [sponsorDropdown, setSponsorDropdown] = useState([]);
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [referralDropdown, setReferralDropdown] = useState([]);
  const [openPrint, setOpenPrint] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState([]);

  const schema = Yup.object().shape({
    from_date: Yup.string().required(' '),
    to_date: Yup.string().required(' ')
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      from_date: '',
      to_date: '',
      sponsor: []
    },
    mode: 'all'
  });

  const { watch, control, setValue } = methods;
  const doctorScope = useDoctorScope();
  useEffect(() => {
    if (doctorScope.isDoctor && doctorScope.doctorId) {
      setValue('doctor', doctorScope.doctorId);
    }
  }, [doctorScope.isDoctor, doctorScope.doctorId, setValue]);

  const getSponsorDropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('sponsor');
      setSponsorDropdown(data);
    } catch (error) {
      console.error('Error fetching sponsor data:', error);
    }
  }, []);

  const getDoctorDropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('doctor');
      setDoctorDropdown(data);
    } catch (error) {
      console.error('Error fetching Doctor data:', error);
    }
  }, []);

  const getReferralDropdown = useCallback(async () => {
    const data = await dropdownFetch('referral');
    if (!selectedRecord) {
      const [defaultReferal] = data.filter(({ status }) => status === 'default');
      setValue('ref_by', defaultReferal?._id);
    }
    setReferralDropdown(data);
    console.log('Hey', referralDropdown);
  }, []);

  useEffect(
    () => {
      getSponsorDropdown();
      getDoctorDropdown();
      getReferralDropdown();
    },
    [getSponsorDropdown],
    [getDoctorDropdown],
    [getReferralDropdown]
  );

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
  const handleExcelReport = async () => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const response = await request(
      '/print/hospital/outpatient-management/doctor-ledger-report',
      { method: 'GET', query: { refId: JSON.stringify({ doctor: watch('doctor'), from_date: watch('from_date'), to_date: watch('to_date') }) } },
      true
    );
    if (!response.success) {
      toast(`Something Went Wrong`, { variant: 'error' });
      return;
    }

    const opdRows     = response?.data?.opdData?.data || [];
    const opdBillRows = response?.data?.opdBillingData?.data || [];
    const ipdRows     = response?.data?.ipdBillingData?.data || [];

    if (opdRows.length === 0 && opdBillRows.length === 0 && ipdRows.length === 0) {
      toast(`No Data Found For These Filter`, { variant: 'error' });
      return;
    }

    const num = (v) => parseFloat(v) || 0;
    const HEADER = ['Sr. No.', 'Bill Date', 'Patient Name', 'UHID', 'Adm / OP No.', 'Address', 'Mobile', 'Service', 'Pay Mode', 'Panel', 'Bill Amt.', 'Disc.', 'Net Amt.'];

    const doctorName =
      opdRows[0]?.consultant?.name ||
      opdBillRows[0]?.patient?.consultant?.name ||
      ipdRows[0]?.patient?.consultant?.name || '';

    const rows = [];
    rows.push([`DOCTOR LEDGER REPORT — ${doctorName}`]);
    rows.push([`Period: ${formatDate(watch('from_date'))} to ${formatDate(watch('to_date'))}`]);
    rows.push([]);

    // Builds one section: header + data rows + section total. Returns {bill, disc, net}.
    // Bill-level disc_amt repeats on every unwound service row — counted once per bill _id.
    const pushSection = (title, data, mapFn, hasBillDisc) => {
      const total = { bill: 0, itemDisc: 0, billDisc: 0 };
      const seen = {};
      rows.push([title]);
      rows.push(HEADER);
      data.forEach((item, i) => {
        const r = mapFn(item, i);
        total.bill += r.bill;
        total.itemDisc += r.disc;
        if (hasBillDisc && item?._id && !seen[item._id]) {
          seen[item._id] = true;
          total.billDisc += num(item?.disc_amt);
        }
        rows.push([i + 1, formatDate(item?.date), r.patient, r.uhid, r.adm, r.address, r.mobile, r.service, r.payMode, r.panel, r.bill, r.disc, r.bill - r.disc]);
      });
      if (data.length === 0) rows.push(['No records found.']);
      if (hasBillDisc && total.billDisc > 0) {
        rows.push(['', '', '', '', '', '', '', '', '', 'Bill-level Discount (once per bill):', '', total.billDisc, '']);
      }
      const disc = total.itemDisc + total.billDisc;
      rows.push(['', '', '', '', '', '', '', '', '', `${title} Total:`, total.bill, disc, total.bill - disc]);
      rows.push([]);
      return { bill: total.bill, disc, net: total.bill - disc };
    };

    const opdTotal = pushSection('OPD CONSULTANCY', opdRows, (item) => ({
      patient: `${item?.name_suffix || ''} ${item?.first_name || ''} ${item?.last_name || ''}`.trim(),
      uhid:    item?.uhid || '',
      adm:     item?.adm_no_date || item?.op_no || '',
      address: item?.address?.address || '',
      mobile:  item?.address?.mobile || '',
      service: 'Consultancy',
      payMode: item?.payment_mode || '',
      panel:   item?.sponsor?.name || '',
      bill:    num(item?.reg),
      disc:    num(item?.disc)
    }), false);

    const opdBillTotal = pushSection('OPD BILLING', opdBillRows, (item) => ({
      patient: `${item?.patient?.name_suffix || ''} ${item?.patient?.first_name || ''} ${item?.patient?.last_name || ''}`.trim(),
      uhid:    item?.uhid || item?.patient?.uhid || '',
      adm:     item?.patient?.adm_no_date || '',
      address: item?.patient?.address?.address || '',
      mobile:  item?.patient?.address?.mobile || '',
      service: item?.serviceDetail?.service_name || item?.serviceDetail?.type || '',
      payMode: item?.pay_mode || '',
      panel:   item?.patient?.sponsor?.name || '',
      bill:    num(item?.service_items?.amount),
      disc:    num(item?.service_items?.disc_amt)
    }), true);

    const ipdTotal = pushSection('IPD BILLING', ipdRows, (item) => ({
      patient: `${item?.patient?.name_suffix || ''} ${item?.patient?.first_name || ''} ${item?.patient?.last_name || ''}`.trim(),
      uhid:    item?.patient?.uhid || '',
      adm:     item?.ipd_no || '',
      address: item?.patient?.address?.address || '',
      mobile:  item?.patient?.address?.mobile || '',
      service: item?.serviceDetail?.service_name || item?.serviceDetail?.type || '',
      payMode: item?.pay_mode || '',
      panel:   item?.patient?.sponsor?.name || '',
      bill:    num(item?.service_items?.amount),
      disc:    num(item?.service_items?.disc_amt)
    }), true);

    // Final calculation — same math as both PDFs: Net = Bill - Disc per section, grand = sum of nets
    const grandBill = opdTotal.bill + opdBillTotal.bill + ipdTotal.bill;
    const grandDisc = opdTotal.disc + opdBillTotal.disc + ipdTotal.disc;
    rows.push(['FINAL CALCULATION', '', 'Bill Amt.', 'Discount', 'Net Amt.']);
    rows.push(['OPD Consultancy', '', opdTotal.bill, opdTotal.disc, opdTotal.net]);
    rows.push(['OPD Billing', '', opdBillTotal.bill, opdBillTotal.disc, opdBillTotal.net]);
    rows.push(['IPD Billing', '', ipdTotal.bill, ipdTotal.disc, ipdTotal.net]);
    rows.push(['GRAND TOTAL (NET)', '', grandBill, grandDisc, grandBill - grandDisc]);
    rows.push([]);
    rows.push(['* Discount includes both service-item level and bill-level discount (bill-level counted once per bill).']);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 7 }, { wch: 11 }, { wch: 24 }, { wch: 10 }, { wch: 12 }, { wch: 26 },
      { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 12 }
    ];
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: fileType });
    saveAs(blob, `Doctor_Ledger_Report_${watch('from_date')}_to_${watch('to_date')}${fileExtension}`);
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Doctor Ledger Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={() =>
                    window.open(
                      `/print${pathname}?refId=${JSON.stringify({ doctor: watch('doctor'), from_date: watch('from_date'), to_date: watch('to_date') })}`,
                      '_blank'
                    )
                  }
                >
                  View Pdf Detail
                </Button>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={() =>
                    window.open(
                      `/print${pathname}/summary?refId=${JSON.stringify({ doctor: watch('doctor'), from_date: watch('from_date'), to_date: watch('to_date') })}`,
                      '_blank'
                    )
                  }
                >
                  View Summary
                </Button>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={handleExcelReport}
                >
                  Download Excel Report
                </Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="doctor"
                      menus={
                        doctorScope.isDoctor
                          ? doctorDropdown.filter((d) => d._id === doctorScope.doctorId)
                          : doctorDropdown
                      }
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Ref By</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="ref_by"
                      menus={[
                        { name: 'All', _id: 'all' }, // Adding the "All" option
                        ...referralDropdown.map((data) => ({ ...data, name: data.referral_name }))
                      ]}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sponsor</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="sponsor"
                      menus={[
                        { name: 'All', _id: 'all' }, // Adding the "All" option
                        ...sponsorDropdown
                      ]}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="from_date" type="date" />
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="to_date" type="date" />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
